import { Skeleton, SkeletonItem, Tag } from '@fluentui/react-components';
import {
  Add20Filled,
  Add20Regular,
  Delete20Regular,
  Link20Regular,
  PeopleTeam20Regular,
} from '@fluentui/react-icons';
import { AzureGroup, ClassCourse, TimePattern, UserListSorting } from '@mnspro-cloud/services';
import { ClassBasicResponse, Person } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { TFunction } from 'i18next';
import { get } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../Button';
import { Flex } from '../../../Flex';
import { Text } from '../../../Text';
import { Tooltip } from '../../../Tooltip';

import { CommunicationMessage, Expandable, Illustrations, IllustrationSize } from '../../..';
import CustomSplitButton from '../../../CustomSplitButton/CustomSplitButton';
import { ThemeMode, useProviderTheme } from '../../../hooks/useProviderTheme';
import { ClassCourse as ClassCourseComponent } from './ClassCourse';
import styles from './ClassCourses.module.css';

export interface ClassCoursesProps {
  courses: ClassCourse[];
  isLoading?: boolean;
  teachers?: Person[];
  isLoadingTeachers?: boolean;
  groups?: AzureGroup[];
  preferenceSorting?: UserListSorting;
  isLoadingGroups?: boolean;
  timepattern?: TimePattern;
  isLoadingTimepattern?: boolean;
  currentClassId?: string;
  onAddExistingCourseClicked?(): void;
  onCreateCourseClicked?(): void;
  onCreateCourseFromAzureAdClicked?(): void;
  /**
   * Will be called when a user clicks on "Lehrer:in hinzufügen".
   * This event can be used to lazy load teachers.
   *
   * @memberof ClassCoursesProps
   */
  onAddTeacherClicked?(): void;
  /**
   * Will be called when a user clickes the "Link group" button for a course.
   * This event can be used to lazy load courses.
   *
   * @memberof ClassCoursesProps
   */
  onLinkGroupClicked?(): void;
  onUpdateCourse?(course: ClassCourse): void;
  onRemoveCourse?(course: ClassCourse): void;

  /**
   * Will be fired if the user clicks on a class name pill.
   * It can be used to navigate to the clicked class.
   *
   * @param {ClassBasicResponse} clickedClass
   * @memberof ClassCoursesProps
   */
  onClassClicked?(clickedClass: ClassBasicResponse): void;
}

const renderExtraContent = (
  course: ClassCourse,
  translate: TFunction,
  onClassClick?: (clickedClass: ClassBasicResponse) => void,
  currentClassId?: string,
) => {
  if (!course.classes || course.classes.length <= 1) {
    return null;
  }

  if (course.classes.length < 4) {
    return (
      <Flex align="center" gap="0.5rem" className={styles.includedCoursesMobileView}>
        <Text>{`${translate('another_classes', { defaultValue: 'Weitere Klassen' })}:`}</Text>
        <Flex wrap gap="0.25rem">
          {course.classes
            .filter((c) => c.id !== currentClassId)
            .map((classResponse, index) => (
              <Button
                appearance="transparent"
                size="small"
                key={index}
                onClick={() => onClassClick?.(classResponse)}
              >
                {classResponse.name}
              </Button>
            ))}
        </Flex>
      </Flex>
    );
  }

  return (
    <Tooltip
      trigger={
        <Tag appearance="outline" size="extra-small">
          {translate('settings:classbook:courses:course_used_in_n_other_classes', {
            defaultValue: 'Verwendet in {{count}} Klassen',
            count: course.classes.length,
          })}
        </Tag>
      }
      content={course.classes.map((c) => c.name).join(', ')}
    />
  );
};

export const ClassCourses: React.FC<ClassCoursesProps> = ({
  courses,
  isLoading,
  teachers,
  groups,
  timepattern,
  isLoadingTeachers,
  isLoadingGroups,
  isLoadingTimepattern,
  currentClassId,
  onAddExistingCourseClicked,
  onCreateCourseClicked,
  onCreateCourseFromAzureAdClicked,
  onAddTeacherClicked,
  onLinkGroupClicked,
  onUpdateCourse,
  onRemoveCourse,
  onClassClicked,
  preferenceSorting,
}) => {
  const { t } = useTranslation();
  const { mode } = useProviderTheme();

  return (
    <>
      <Flex direction="column">
        {isLoading ? (
          <Skeleton className={styles.title}>
            <SkeletonItem size={20} style={{ width: '90px' }} />
          </Skeleton>
        ) : (
          <Text weight="bold" className={styles.title}>
            {t('courses', { defaultValue: 'Kurse' })}
          </Text>
        )}
        {isLoading ? (
          <Skeleton className={styles.addButton}>
            <SkeletonItem size={32} style={{ width: '130px' }} />
          </Skeleton>
        ) : (
          <CustomSplitButton
            data-testid="addCourseMenuButton"
            className={styles.addButton}
            menu={[
              {
                'data-testid': 'createCourseButton',
                id: 'settings:classbook:course:button_create_course',
                key: 'settings:classbook:course:button_create_course',
                content: t('settings:classbook:course:button_create_course', {
                  defaultValue: 'Neu erstellen (Leer)',
                }),
                icon: (
                  <Add20Regular
                    aria-label={t('settings:classbook:course:course_button_create_course_label', {
                      defaultValue: 'Öffnet Unterseite zum Erstellen von einem Kurs',
                    })}
                  />
                ),
              },
              {
                'data-testid': 'linkCourseButton',
                id: 'settings:classbook:course:button_existing_course',
                key: 'settings:classbook:course:button_existing_course',
                content: t('settings:classbook:course:button_existing_course', {
                  defaultValue: 'Bestehenden Kurs Auswählen',
                }),
                icon: (
                  <Link20Regular
                    aria-label={t('settings:classbook:course:course_button_existing_course_label', {
                      defaultValue: 'Öffnet Popup zum Auswählen eines bestehendes Kurses',
                    })}
                  />
                ),
              },
              {
                'data-testid': 'fromAzureGroupButton',
                id: 'settings:classbook:course:button_link_course',
                key: 'settings:classbook:course:button_link_course',
                content: t('settings:classbook:course:button_link_course', {
                  defaultValue: 'Aus Azure AD Gruppe erstellen',
                }),
                icon: (
                  <PeopleTeam20Regular
                    aria-label={t('settings:classbook:course:course_button_link_course_label', {
                      defaultValue: 'Öffnet Popup zum Auswählen eines bestehender Azure Gruppe',
                    })}
                  />
                ),
              },
            ]}
            button={{
              content: t('settings:classbook:course:button_add_course', {
                defaultValue: 'Kurs hinzufügen',
              }),
              icon: <Add20Filled />,
              'aria-roledescription': 'splitbutton',
              'aria-describedby': 'instruction-message-primary-button',
            }}
            primary
            onMenuItemClick={(_event, data) => {
              const id = get(data, 'id', '') as string;
              if (id === 'settings:classbook:course:button_create_course') {
                onCreateCourseClicked?.();
              } else if (id === 'settings:classbook:course:button_existing_course') {
                onAddExistingCourseClicked?.();
              } else {
                onCreateCourseFromAzureAdClicked?.();
              }
            }}
          />
        )}
        {!isLoading && (!courses || courses.length === 0) && (
          <div className="emptyWrapper">
            <CommunicationMessage
              headline={t('list_empty', { defaultValue: 'Keine Einträge vorhanden' })}
              illustration={Illustrations.Empty}
              useDarkIllustration={mode === ThemeMode.Dark}
              illustrationSize={IllustrationSize.Small}
              text={t('settings:classbook:courses:empty_text', {
                defaultValue: 'Um einen Kurs zuzuordnen, klicken Sie auf „Kurs hinzufügen“.',
              })}
            />
          </div>
        )}
        {isLoading ? (
          <>
            <Skeleton className={styles.expandableCourse}>
              <SkeletonItem size={48} />
            </Skeleton>
            <Skeleton className={styles.expandableCourse}>
              <SkeletonItem size={48} />
            </Skeleton>
            <Skeleton className={styles.expandableCourse}>
              <SkeletonItem size={48} />
            </Skeleton>
          </>
        ) : (
          courses?.map((course) => (
            <div key={course.id} className={styles.expandableCourse}>
              <Expandable
                header={course.name}
                actions={[
                  {
                    icon: (
                      <Delete20Regular
                        aria-label={t('settings:classbook:courses:delete_label', {
                          defaultValue:
                            'Öffnet eine Popup zum Bestätigen des Löschvorgangs des Kurses',
                        })}
                      />
                    ),
                    onClick: () => onRemoveCourse?.(course),
                  },
                ]}
                extraContent={renderExtraContent(course, t, onClassClicked, currentClassId)}
              >
                <ClassCourseComponent
                  {...course}
                  onSave={(c) => onUpdateCourse?.(c)}
                  groups={groups}
                  preferenceSorting={preferenceSorting}
                  teachers={teachers}
                  timePattern={timepattern}
                  isLoadingGroups={isLoadingGroups}
                  isLoadingTeachers={isLoadingTeachers}
                  isLoadingTimePattern={isLoadingTimepattern}
                  onAddTeacherClicked={onAddTeacherClicked}
                  onLinkGroupClicked={onLinkGroupClicked}
                />
              </Expandable>
            </div>
          ))
        )}
      </Flex>
    </>
  );
};
